import { get } from 'svelte/store';
import { queryFilters } from '$lib/stores/selection';
import { apiClient } from '$lib/api/client';
import type { FilterResponse } from '$lib/types/api';
import type { Company } from '$lib/types';
import { turnstile, guardToken } from '$lib/turnstile';

/**
 * Filter a specific set of companies based on current filters
 * Used when applying filters to a mixed set of results (search + similar)
 */
export async function filterCompanies(companyIds: string[]): Promise<FilterResponse> {
  const filters = get(queryFilters);

  // Guarded, like every route that returns sellable rows. Missing here, so
  // filtering a mixed set — search results plus the companies found similar to
  // them — was refused.
  const payload = {
    company_ids: companyIds,
    filters: filters,
    contact_mode: filters.contact_mode || 'single',
    turnstile_token: await guardToken()
  };

  const response = await apiClient.post<FilterResponse>('/deep-iq/filter-companies', payload);
  return response.data;
}

/**
 * Find companies similar to a single company using centroid similarity
 * Applies current filters to ensure results match user criteria
 */
export async function findSimilarCompanies(companyId: string): Promise<FilterResponse> {
  const filters = get(queryFilters);

  // Get Turnstile token
  let turnstileToken: string | undefined;
  try {
    turnstileToken = await turnstile.getToken();
  } catch {
    // Continue without token - let backend decide if it's required
  }

  const payload = {
    company_id: companyId,
    filters: filters,
    contact_mode: filters.contact_mode || 'single',
    turnstile_token: turnstileToken
  };

  const response = await apiClient.post<FilterResponse>('/deep-iq/centroid-single', payload);
  return response.data;
}

/**
 * Helper to extract company IDs from results
 */
export function extractCompanyIds(results: Company[]): string[] {
  return results.map((company) => company.id).filter(Boolean);
}
